import React, { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation } from 'react-router-dom';
import { X, Globe2, Shield, AlertCircle } from 'lucide-react';
import { auth0Connections } from '../auth/auth0-config';
import './DevAuthEntryScreen.css';

type PlatformKind = 'ios' | 'android' | 'unknown';

const IOSShareIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 4L12 15M12 4L8 8M12 4L16 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path
      d="M5 13V19C5 19.5304 5.21071 20.0391 5.58579 20.4142C5.96086 20.7893 6.46957 21 7 21H17C17.5304 21 18.0391 20.7893 18.4142 20.4142C18.7893 20.0391 19 19.5304 19 19V13"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const AndroidMenuIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="5" r="1.5" fill="currentColor" />
    <circle cx="12" cy="12" r="1.5" fill="currentColor" />
    <circle cx="12" cy="19" r="1.5" fill="currentColor" />
  </svg>
);

const detectPlatform = (): PlatformKind => {
  const ua = navigator.userAgent.toLowerCase();
  if (/iphone|ipad|ipod/.test(ua)) return 'ios';
  if (/android/.test(ua)) return 'android';
  return 'unknown';
};

export const DevAuthEntryScreen: React.FC = () => {
  const { loginWithRedirect } = useAuth0();
  const location = useLocation();

  const [platform, setPlatform] = useState<PlatformKind>('unknown');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [userFriendlyError, setUserFriendlyError] = useState<string | null>(null);

  useEffect(() => {
    setPlatform(detectPlatform());
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const auth0Error = urlParams.get('error');
    const auth0ErrorDescription = urlParams.get('error_description');

    if (auth0Error) {
      let friendlyMessage = 'Authentication failed. Please try again.';
      if (auth0Error === 'invalid_request' && auth0ErrorDescription?.includes('user already exists')) {
        friendlyMessage = 'This email is already registered. Please sign in instead.';
      } else if (auth0ErrorDescription?.includes('already exists') || auth0ErrorDescription?.includes('user_exists')) {
        friendlyMessage = 'This email is already registered. Please sign in instead.';
      }

      setUserFriendlyError(friendlyMessage);
      setTimeout(() => {
        setUserFriendlyError(null);
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
      }, 8000);
    }
  }, [location.search]);

  const handleGoogleSignIn = () =>
    loginWithRedirect({
      authorizationParams: {
        connection: auth0Connections.google,
        prompt: 'select_account',
      },
    });

  const handleEmailSignIn = () =>
    loginWithRedirect({
      authorizationParams: {
        connection: auth0Connections.email,
        screen_hint: 'signin',
      },
    });

  const handleEmailSignUp = () =>
    loginWithRedirect({
      authorizationParams: {
        connection: auth0Connections.email,
        screen_hint: 'signup',
      },
    });

  const links = useMemo(
    () => [
      { label: 'CoachCedar.com', href: 'https://coachcedar.com', Icon: Globe2 },
      { label: 'Privacy Policy', href: 'https://coachcedar.com/privacy', Icon: Shield },
    ],
    []
  );

  return (
    <div className="dev-auth-shell">
      <div className="dev-auth-inner">
        <header className="dev-auth-header">
          {/* Bubble 1: Greeting (narrower) */}
          <div className="dev-auth-bubble dev-auth-bubble-narrow" role="note" aria-label="Coach Cedar greeting">
            <div className="dev-auth-bubble-avatar" aria-hidden="true">
              <span className="dev-auth-bubble-avatar-text">CC</span>
            </div>
            <div className="dev-auth-bubble-content">
              <div className="dev-auth-bubble-title">Hi, I'm Coach Cedar!</div>
              <div className="dev-auth-bubble-subtitle">Welcome to the Coach Cedar Beta</div>
            </div>
          </div>

          {/* Bubble 2: Beta explanation */}
          <div className="dev-auth-bubble dev-auth-bubble-narrow-2" role="note" aria-label="Beta setup explanation">
            <div className="dev-auth-bubble-avatar" aria-hidden="true">
              <span className="dev-auth-bubble-avatar-text">CC</span>
            </div>
            <div className="dev-auth-bubble-content">
              <div className="dev-auth-bubble-text">
              <strong>One click setup step:</strong><br />For the beta phase, we need you to <strong>add Coach Cedar to your phone&apos;s home screen</strong> before logging in.
                This ensures the best experience and prevents authentication issues.
              </div>
            </div>
          </div>

          {/* Bubble 3: Instructions */}
          <div className="dev-auth-bubble dev-auth-bubble-narrow-2" role="note" aria-label="Mobile instructions">
            <div className="dev-auth-bubble-avatar" aria-hidden="true">
              <span className="dev-auth-bubble-avatar-text">CC</span>
            </div>
            <div className="dev-auth-bubble-content">
              <div className="dev-auth-bubble-section-title">Mobile Instructions</div>

              {platform === 'ios' && (
                <div className="dev-auth-card dev-auth-card-warn">
                  <strong>iOS:</strong> Tap <span className="dev-auth-chip blue"><IOSShareIcon /> Share</span>, then "Add to Home Screen"
                </div>
              )}

              {platform === 'android' && (
                <div className="dev-auth-card dev-auth-card-info">
                  <strong>Android:</strong> Tap <span className="dev-auth-chip gray"><AndroidMenuIcon /> Menu</span>, then "Add to Home screen" / "Install app"
                </div>
              )}

              {platform === 'unknown' && (
                <>
                  <div className="dev-auth-card dev-auth-card-warn">
                    <strong>iOS:</strong> Tap <span className="dev-auth-chip blue"><IOSShareIcon /> Share</span>, then "Add to Home Screen"
                  </div>
                  <div className="dev-auth-card dev-auth-card-info">
                    <strong>Android:</strong> Tap <span className="dev-auth-chip gray"><AndroidMenuIcon /> Menu</span>, then "Add to Home screen" / "Install app"
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Bubble 4: Why do this */}
          <div className="dev-auth-bubble dev-auth-bubble-narrow-3" role="note" aria-label="Why add to home screen">
            <div className="dev-auth-bubble-avatar" aria-hidden="true">
              <span className="dev-auth-bubble-avatar-text">CC</span>
            </div>
            <div className="dev-auth-bubble-content">
              <div className="dev-auth-bubble-text">
                <strong>Why do this?</strong> <br />Installing Coach Cedar as an app ensures your login stays active and your conversation history persists.
                It also gives you the best mobile experience with offline support and push notifications.
              </div>
            </div>
          </div>

          {/* CTA button outside bubbles */}
          <button className="dev-auth-cta" onClick={() => setDrawerOpen(true)}>
            ✅ Let&apos;s go
          </button>

          {userFriendlyError && (
            <div className="dev-auth-alert" role="alert">
              <AlertCircle size={16} />
              <span>{userFriendlyError}</span>
            </div>
          )}
        </header>
      </div>

      {/* Bottom nav-like links (matches app nav affordance) */}
      <nav className="dev-auth-bottom-nav" aria-label="External links">
        <div className="dev-auth-bottom-nav-inner">
          {links.map(({ label, href, Icon }) => (
            <a key={href} className="dev-auth-nav-item" href={href} target="_blank" rel="noopener noreferrer">
              <div className="dev-auth-nav-icon">
                <Icon size={18} />
              </div>
              <div className="dev-auth-nav-label">{label}</div>
            </a>
          ))}
        </div>
      </nav>

      {/* Sign-in drawer */}
      <div className={`dev-auth-drawer-overlay ${drawerOpen ? 'open' : ''}`} aria-hidden={!drawerOpen}>
        <div className={`dev-auth-drawer ${drawerOpen ? 'open' : ''}`} role="dialog" aria-label="Sign in drawer">
          <div className="dev-auth-drawer-header">
            <div className="dev-auth-drawer-title">Sign in to continue</div>
            <button className="dev-auth-drawer-close" onClick={() => setDrawerOpen(false)} aria-label="Close">
              <X size={18} />
            </button>
          </div>

          <div className="dev-auth-drawer-body">
            <button className="dev-auth-btn dev-auth-btn-plain" onClick={handleGoogleSignIn}>
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Continue with Google
            </button>

            <div className="dev-auth-divider">or</div>

            <button className="dev-auth-btn dev-auth-btn-secondary" onClick={handleEmailSignIn}>
              Sign in with Email
            </button>
            <button className="dev-auth-btn dev-auth-btn-primary" onClick={handleEmailSignUp}>
              Create Account
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


